import React, { FC } from 'react';
import { StepperContainer, StepperWrapper, StepItem, StepCircle, StepLabel, StepConnector, CheckIcon } from './style';

export interface Step {
  id: string;
  label: string;
  /** If true, this step will be hidden from the stepper */
  hidden?: boolean;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  /** Allow clicking on completed steps to navigate back */
  allowBackNavigation?: boolean;
}

const Stepper: FC<StepperProps> = ({ steps, currentStep, onStepClick, allowBackNavigation = true }) => {
  // Filter out hidden steps for display
  const visibleSteps = steps.filter((step) => !step.hidden);

  // Map the current step index to the visible steps array
  const getVisibleStepIndex = (originalIndex: number): number => {
    let visibleIndex = 0;
    for (let i = 0; i < originalIndex && i < steps.length; i++) {
      if (!steps[i].hidden) {
        visibleIndex++;
      }
    }
    return visibleIndex;
  };

  const currentVisibleStep = getVisibleStepIndex(currentStep);

  const handleStepClick = (visibleIndex: number) => {
    if (!onStepClick || !allowBackNavigation) return;

    // Only allow clicking on completed steps
    if (visibleIndex < currentVisibleStep) {
      // Convert visible index back to original index
      let originalIndex = 0;
      let visibleCount = 0;
      for (let i = 0; i < steps.length; i++) {
        if (!steps[i].hidden) {
          if (visibleCount === visibleIndex) {
            originalIndex = i;
            break;
          }
          visibleCount++;
        }
      }
      onStepClick(originalIndex);
    }
  };

  return (
    <StepperContainer>
      <StepperWrapper>
        {visibleSteps.map((step, index) => {
          const isCompleted = index < currentVisibleStep;
          const isActive = index === currentVisibleStep;
          const isClickable = allowBackNavigation && isCompleted && !!onStepClick;

          return (
            <React.Fragment key={step.id}>
              <StepItem
                $isActive={isActive}
                $isCompleted={isCompleted}
                $isClickable={isClickable}
                onClick={() => handleStepClick(index)}
              >
                <StepCircle $isActive={isActive} $isCompleted={isCompleted} $isClickable={isClickable}>
                  {isCompleted ? (
                    <CheckIcon viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </CheckIcon>
                  ) : (
                    index + 1
                  )}
                </StepCircle>
                <StepLabel $isActive={isActive} $isCompleted={isCompleted} $isClickable={isClickable}>
                  {step.label}
                </StepLabel>
              </StepItem>
              {index < visibleSteps.length - 1 && <StepConnector $isCompleted={index < currentVisibleStep} />}
            </React.Fragment>
          );
        })}
      </StepperWrapper>
    </StepperContainer>
  );
};

export default Stepper;
